import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { ServiceProviderReceiptBatchOverviewTableView } from './ServiceProviderReceiptBatchOverviewTableView';
import {
  transformPhoenixListToLegacyList,
  useGenerateReceiptBatchPdf,
  useGetReceiptBatchListPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import { useTrackStructuredEvent } from '@pflegenavi/frontend/tracking';
import type { ReceiptBatchState } from '@pflegenavi/service-provider-shared/api-endpoints';
import { ReceiptBatchTableEnlargeGallery } from './ReceiptBatchTableEnlargeGallery';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';
import { useGetBatchReceiptDateString } from '@pflegenavi/shared/frontend';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { useHandleFailedPreviewGenerationError } from '../../hooks/useHandleFailedPreviewGenerationError';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ServiceProviderReceiptBatchOverviewTableProps {}

export const ServiceProviderReceiptBatchOverviewTable: FC<
  ServiceProviderReceiptBatchOverviewTableProps
> = () => {
  const { t } = useTranslation();

  const stripeVisualizationEnabled = useFeatureFlagEnabled(
    FeatureFlag.dev_StripeVisualization
  );

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [tab, setTab] = useState<ReceiptBatchState | 'all'>('all');
  const [receiptDateRange, setReceiptDateRange] = useState<
    | {
        from?: Date;
        to?: Date;
      }
    | undefined
  >(undefined);
  const [acceptedDateRange, setAcceptedDateRange] = useState<
    | {
        from?: Date;
        to?: Date;
      }
    | undefined
  >(undefined);
  const [amountRange, setAmountRange] = useState<
    | {
        from?: number;
        to?: number;
      }
    | undefined
  >(undefined);
  const [receiptCountRange, setReceiptCountRange] = useState<
    | {
        from?: number;
        to?: number;
      }
    | undefined
  >(undefined);

  const { data: phoenixData } = useGetReceiptBatchListPhoenix({
    page: page + 1,
    page_size: pageSize,
    order_by: ['updated_on'],
    state: tab === 'all' ? undefined : [tab],
    receipt_date: receiptDateRange,
    accepted_date: acceptedDateRange,
  });

  const finalData =
    phoenixData && transformPhoenixListToLegacyList(phoenixData);

  const { getBatchReceiptDateString } = useGetBatchReceiptDateString();

  const transformedData = useMemo(() => {
    return (
      finalData?.data?.map((receiptBatch) => {
        return {
          ...receiptBatch,
          receiptDateString: getBatchReceiptDateString(
            receiptBatch.useIndividualDates,
            receiptBatch.minReceiptDate,
            receiptBatch.maxReceiptDate,
            receiptBatch.receiptDate
          ),
        };
      }) ?? []
    );
  }, [finalData?.data, getBatchReceiptDateString]);

  const [selectedBatchTenant, setSelectedBatchTenant] = useState<
    string | undefined
  >(undefined);
  const [receiptImageIds, setReceiptImageIds] = useState<string[] | undefined>(
    undefined
  );
  const [imageGalleryTitle, setImageGalleryTitle] = useState<
    string | undefined
  >(undefined);

  const [openImageGallery, setOpenImageGallery] = useState(false);

  const [generatedReceiptImageUrls, setGeneratedReceiptImageUrls] = useState<
    string[]
  >([]);
  const [generatedReceiptTypeMap, setGeneratedReceiptTypeMap] =
    useState<UrlTypeNameMapping>(new Map());

  const { mutateAsync: generateReceiptBatch } = useGenerateReceiptBatchPdf();
  const { handleFailedPreviewGenerationError } =
    useHandleFailedPreviewGenerationError();

  const generateReceiptBatchPreview = useCallback(
    async (batchId: string, filename: string) => {
      try {
        const receiptBlob = await generateReceiptBatch({
          batchId,
        });
        if (receiptBlob) {
          const file = window.URL.createObjectURL(receiptBlob);

          setGeneratedReceiptImageUrls([file]);
          setGeneratedReceiptTypeMap(
            new Map([
              [
                file,
                {
                  type: 'application/pdf',
                  data: Promise.resolve(receiptBlob),
                  name: filename,
                },
              ],
            ])
          );
          setOpenImageGallery(true);
        }
      } catch (e) {
        handleFailedPreviewGenerationError(e);
      }
    },
    [generateReceiptBatch, handleFailedPreviewGenerationError]
  );

  const onClickReceiptIcon = useCallback(
    async ({
      id,
      tenant,
      receiptImageIds,
      title,
      isPreview,
    }: {
      id: string;
      tenant: string;
      receiptImageIds: string[] | undefined;
      title: string;
      isPreview?: boolean;
    }) => {
      if (isPreview) {
        await generateReceiptBatchPreview(id, title); // Title contains the filename when in preview mode
        return;
      }
      setSelectedBatchTenant(tenant);
      setReceiptImageIds(receiptImageIds);
      setImageGalleryTitle(title);
      if (receiptImageIds && receiptImageIds.length > 0) {
        setOpenImageGallery(true);
      }
    },
    [generateReceiptBatchPreview]
  );

  return (
    <>
      {stripeVisualizationEnabled && (
        <Typography variant="h6">
          {t('sp-web-app.receipt-batches-overview')}
        </Typography>
      )}
      <ServiceProviderReceiptBatchOverviewTableView
        data={transformedData}
        totalRowCount={finalData?.meta?.totalItems ?? 0}
        page={page}
        pageSize={pageSize}
        onChangePage={setPage}
        onChangePageSize={setPageSize}
        onClickReceiptIcon={onClickReceiptIcon}
        receiptDateRange={receiptDateRange}
        onChangeReceiptDateRange={setReceiptDateRange}
        acceptedDateRange={acceptedDateRange}
        onChangeSubmissionDateRange={setAcceptedDateRange}
        amountRange={amountRange}
        onChangeAmountRange={(range) =>
          setAmountRange(
            range
              ? {
                  from: range.from ? range.from * 100 : undefined,
                  to: range.to ? range.to * 100 : undefined,
                }
              : undefined
          )
        }
        receiptCountRange={receiptCountRange}
        onChangeReceiptCountRange={setReceiptCountRange}
        tab={tab}
        setTab={setTab}
        useTrackStructuredEvent={useTrackStructuredEvent}
      />
      <ReceiptBatchTableEnlargeGallery
        selectedBatchTenant={selectedBatchTenant}
        openImageGallery={openImageGallery}
        setOpenImageGallery={setOpenImageGallery}
        batchImageIds={receiptImageIds}
        title={imageGalleryTitle ?? ''}
        generatedReceiptImageUrls={generatedReceiptImageUrls}
        setGeneratedReceiptImageUrls={setGeneratedReceiptImageUrls}
        generatedReceiptTypeMap={generatedReceiptTypeMap}
      />
    </>
  );
};
